import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import { crewWording, loadCrew, namesFor } from '../lib/crew'
import Packet from './Packet'
import { loadProject, saveProject } from '../lib/db'
import {
  clock,
  lengthOf,
  NOT_ON_THE_STOP,
  onDay,
  spanOf,
  stopState,
  timeOnSite,
  VISIT_KINDS,
  windowOf,
  type StopState,
  type Visit,
} from '../lib/visits'

/**
 * WHERE YOU ARE GOING TODAY, AND WHETHER YOU HAVE BEEN.
 *
 * TOD-02. The stop card, and it is on Today rather than on a screen of its
 * own. TOD-02 is the crew's view of TOD-01, and this product has one login and
 * no roles: a separate Crew Today with the same content and no way to be a
 * crew member would be a duplicate screen, and TOD-01 asks for today's visits
 * on this page anyway.
 *
 * START AND FINISH ARE THE FIRST RECORD THAT A JOB HAPPENED. Everything before
 * them describes what was arranged. A visit can be agreed and never happen,
 * and can happen having never been agreed, so they are stamps rather than
 * another state on the same field. They are written by a person pressing a
 * button while standing there, never by the clock passing the window: a window
 * that has gone by with nothing pressed says exactly that, and is not a
 * failure, because forgetting to tap is the ordinary case.
 *
 * WHAT IS NOT HERE IS NAMED. TOD-02 wants only assigned work, a route with a
 * timestamped ETA, and an offline packet with a download step. There is one
 * login, no map, and jobs and photographs are already on this device, so all
 * three would be theatre. See NOT_ON_THE_STOP.
 */
export default function Stops({
  items,
  onChanged,
  onOpenJob,
  compact = false,
  onSeeWeek,
}: {
  items: ProjectSummary[]
  onChanged: () => void
  onOpenJob: (job: ProjectSummary) => void
  /** The dashboard panel rather than the standing-at-a-house screen. */
  compact?: boolean
  onSeeWeek?: () => void
}) {
  const [busy, setBusy] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [roster] = useState(() => loadCrew())
  /* The packet, opened from the stop somebody is standing at. See ui/Packet. */
  const [packet, setPacket] = useState<{ jobId: string; who: string; visit: Visit } | null>(null)

  const now = new Date()
  const day = now.toISOString().slice(0, 10)
  const minutes = now.getHours() * 60 + now.getMinutes()

  const rows = useMemo(() => {
    const all = items.flatMap((j) => (j.visits ?? []).map((v) => ({ v, j })))
    const mine = onDay(day, all.map((x) => x.v))
    return mine.map((v) => {
      const found = all.find((x) => x.v.id === v.id)!
      return { v, j: found.j }
    })
  }, [items, day])

  /*
   * THE NEXT ONE IS THE ONE YOU ARE STANDING AT, or the one you are driving
   * to. A stop somebody is on site at outranks the clock, because that is
   * where the person actually is.
   */
  const next = useMemo(() => {
    const onsite = rows.find((r) => stopState(r.v, { day, minutes }) === 'onsite')
    if (onsite) return onsite
    return (
      rows.find((r) => stopState(r.v, { day, minutes }) === 'booked') ??
      rows.find((r) => stopState(r.v, { day, minutes }) === 'unmarked')
    )
  }, [rows, day, minutes])

  if (rows.length === 0) return null

  const write = async (jobId: string, id: string, patch: Partial<Visit>) => {
    setBusy('Saving...')
    const doc = await loadProject(jobId)
    if (!doc) {
      setBusy('That job could not be opened on this device.')
      setTimeout(() => setBusy(''), 3000)
      return
    }
    await saveProject({
      ...doc,
      visits: (doc.visits ?? []).map((v) => (v.id === id ? { ...v, ...patch } : v)),
    })
    setBusy('')
    onChanged()
  }

  const who = (j: ProjectSummary) => j.customer.trim() || j.address.trim() || j.name
  const kindOf = (v: Visit) => VISIT_KINDS.find((k) => k.id === v.kind)?.label ?? 'Visit'

  const done = rows.filter((r) => stopState(r.v, { day, minutes }) === 'done').length
  const rest = rows.filter((r) => r.v.id !== next?.v.id)
  /*
   * A day is six stops or it is a warehouse. Collapsing at three produced a
   * link reading Show the other 1, which costs a line to save a line.
   */
  const shown = showAll || rest.length <= 6 ? rest : rest.slice(0, 5)

  const said = (s: StopState, v: Visit) => {
    if (s === 'done') {
      const mins = timeOnSite(v)
      // Said the way a person says it: 53 min, not 0h 53m.
      return mins === null ? 'Finished.' : `Finished. ${spanOf(mins)} on site.`
    }
    if (s === 'onsite') return `On site since ${clock(startMinutes(v))}.`
    if (s === 'unmarked') return 'The window has gone by and nothing was marked.'
    // The window is already the first line of the card, so this says the one
    // thing it does not: how long the day has this house down for.
    return `Booked for ${lengthOf(v)}.`
  }

  /*
   * TWO SHAPES, ONE COMPONENT.
   *
   * Today is a dashboard: four panels, and a stop is two lines and one button
   * on it. Today's stops is its own screen for a person standing at a house,
   * and there a stop is the whole card with the phone number, the access note
   * and both timestamps. Same data, same rules, same wording; what changes is
   * how much of it a screen has room to be about.
   */
  if (compact) {
    return (
      <div className="card ws-p stops-compact">
        <div className="ws-ph">
          <h3>Today's stops</h3>
          <button className="link-btn" onClick={onSeeWeek}>
            The week
          </button>
        </div>
        {busy && <p className="ws-said">{busy}</p>}
        {rows.map(({ v, j }) => {
          const st = stopState(v, { day, minutes })
          const isNext = v.id === next?.v.id
          const crew = crewWording(namesFor(roster, v.assignedTo ?? []))
          return (
            <div key={v.id} className="stop-line">
              <div>
                <b>
                  {who(j)}, {kindOf(v).toLowerCase()}
                </b>
                <small>
                  {windowOf(v)}
                  {j.address.trim() && ` · ${j.address.trim()}`}
                  {crew && ` · ${crew}`}
                </small>
              </div>
              {st === 'onsite' ? (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => void write(j.id, v.id, { doneAt: new Date().toISOString() })}
                >
                  Finish the visit
                </button>
              ) : isNext && st !== 'done' ? (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => void write(j.id, v.id, { startedAt: new Date().toISOString() })}
                >
                  Start the visit
                </button>
              ) : v.kind === 'install' ? (
                <button
                  className="btn btn-quiet btn-sm"
                  onClick={() => setPacket({ jobId: j.id, who: who(j), visit: v })}
                >
                  What goes up
                </button>
              ) : (
                <button className="btn btn-quiet btn-sm" onClick={() => onOpenJob(j)}>
                  Open the job
                </button>
              )}
            </div>
          )
        })}
        {/*
          THE TIMESTAMPS ARE TIMESTAMPS. Two of them, written by a person
          pressing a button while standing there. Nothing in this product turns
          them into hours, into a wage, or into anybody's pay, and a screen that
          records a start and a finish invites exactly that assumption.
        */}
        <p className="ws-pfoot">
          Start and finish record a timestamp. Nothing turns that into hours or pay.
        </p>
        {packet && (
          <Packet
            jobId={packet.jobId}
            who={packet.who}
            visit={packet.visit}
            onClose={() => setPacket(null)}
          />
        )}
      </div>
    )
  }

  return (
    <div className="card pad stops">
      <div className="tod-card-head">
        <p className="ws-eyebrow">
          Today, {rows.length} {rows.length === 1 ? 'stop' : 'stops'}
        </p>
        {done > 0 && (
          <span className="stops-done">
            {done} of {rows.length} finished
          </span>
        )}
      </div>

      {busy && <p className="ws-said">{busy}</p>}

      {next && (
        <StopCard
          v={next.v}
          job={next.j}
          crew={crewWording(namesFor(roster, next.v.assignedTo ?? []))}
          state={stopState(next.v, { day, minutes })}
          who={who(next.j)}
          kind={kindOf(next.v)}
          said={said(stopState(next.v, { day, minutes }), next.v)}
          onOpen={() => onOpenJob(next.j)}
          onPacket={() => setPacket({ jobId: next.j.id, who: who(next.j), visit: next.v })}
          onStart={() => void write(next.j.id, next.v.id, { startedAt: new Date().toISOString() })}
          onFinish={() => void write(next.j.id, next.v.id, { doneAt: new Date().toISOString() })}
        />
      )}

      {rest.length > 0 && (
        <div className="stops-rest">
          {/*
            NOT AFTER THAT. The stop somebody is standing at is the one that is
            live, not the one that is next in the day, so a six o'clock that is
            already finished sits under a seven thirty that is still running.
            After that was a lie about half this list.
          */}
          <p className="ws-eyebrow">The other stops today</p>
          {shown.map(({ v, j }) => {
            const s = stopState(v, { day, minutes })
            return (
              <button key={v.id} className={`tod-row stop-row ${s}`} onClick={() => onOpenJob(j)}>
                <span className="tod-where">
                  {clock(v.from)} · {who(j)}
                </span>
                <span className="tod-said">
                  {kindOf(v)} · {s === 'booked' ? `${windowOf(v)} · ${lengthOf(v)}` : said(s, v)}
                </span>
                <span className="tod-go" aria-hidden="true">
                  &rsaquo;
                </span>
              </button>
            )
          })}
          {rest.length > shown.length && (
            <button className="link-btn" onClick={() => setShowAll(true)}>
              Show the other {rest.length - shown.length}
            </button>
          )}
        </div>
      )}

      {/*
        The first line of this comes off once anybody has been put on a visit:
        it says nothing is assigned because there are no crew records, and
        there are now. The other two are still true.
      */}
      {packet && (
        <Packet
          jobId={packet.jobId}
          who={packet.who}
          visit={packet.visit}
          onClose={() => setPacket(null)}
        />
      )}

      <p className="stops-unknown">
        <b>What a stop does not carry.</b>{' '}
        {(rows.some((r) => (r.v.assignedTo?.length ?? 0) > 0)
          ? NOT_ON_THE_STOP.slice(1)
          : NOT_ON_THE_STOP
        ).join(' ')}
      </p>
    </div>
  )
}

/** Minutes past midnight of the start stamp, for the on site wording. */
function startMinutes(v: Visit): number {
  if (!v.startedAt) return v.from
  const d = new Date(v.startedAt)
  return Number.isFinite(d.getTime()) ? d.getHours() * 60 + d.getMinutes() : v.from
}

/**
 * THE ONE YOU ARE STANDING AT.
 *
 * Everything a person needs while walking up a driveway, in the order they
 * need it: when they said you would be there, whose house it is, the street,
 * and whatever the note said about the gate and the dog. The address is a big
 * quiet line rather than a link, because on a phone it is the thing being read
 * off the screen to somebody.
 */
function StopCard({
  v,
  job,
  crew,
  state,
  who,
  kind,
  said,
  onOpen,
  onPacket,
  onStart,
  onFinish,
}: {
  v: Visit
  job: ProjectSummary
  /** Who is going, said the way a person says it. See lib/crew.ts. */
  crew: string
  state: StopState
  who: string
  kind: string
  said: string
  onOpen: () => void
  onPacket: () => void
  onStart: () => void
  onFinish: () => void
}) {
  return (
    <div className={`stop ${state}`}>
      <p className="stop-when">
        {windowOf(v)}
        <em>{kind}</em>
        {(v.assignedTo?.length ?? 0) > 0 && <em>{crew}</em>}
        {v.state === 'requested' && <b>asked for, not agreed</b>}
      </p>
      <h2 className="stop-who">{who}</h2>
      {job.address.trim() && <p className="stop-where">{job.address}</p>}
      {job.phone.trim() && (
        <p className="stop-phone">
          <a href={`tel:${job.phone.replace(/[^\d+]/g, '')}`}>{job.phone}</a>
        </p>
      )}
      {v.note.trim() && <p className="stop-note">{v.note}</p>}
      <p className="stop-said">{said}</p>

      <div className="stop-acts">
        {/*
          WHAT GOES UP IS ALWAYS OFFERED, finished or not. A crew that has just
          finished still gets asked what was on run four, and Open the job puts
          a photograph and a price in front of somebody who wanted a cut length.
        */}
        <button className="btn" onClick={onPacket}>
          What goes up
        </button>
        {state === 'done' ? (
          <button className="btn" onClick={onOpen}>
            Open the job
          </button>
        ) : (
          <>
            {/*
              ONE BUTTON AT A TIME. Start and Finish side by side is two
              buttons a cold thumb can confuse, and finishing a visit nobody
              started is a record that says a crew was at a house for zero
              minutes.
            */}
            {v.startedAt ? (
              <button className="btn primary" onClick={onFinish}>
                Finish the visit
              </button>
            ) : (
              <button className="btn primary" onClick={onStart}>
                Start the visit
              </button>
            )}
            <button className="btn" onClick={onOpen}>
              Open the job
            </button>
          </>
        )}
      </div>
    </div>
  )
}
